/**
 * When the app is locked, and why.
 *
 * Everything here is synchronous and storage-backed on purpose. The lock has to
 * be decided BEFORE the first render — a provider that waited on a plugin
 * promise would leave `#root` empty, and the OTA updater rolls a bundle back
 * when `#root` is still empty after `appReadyTimeout` (see
 * `shared/ota/confirmBundle.ts`). A perfectly good update would be reverted by
 * the lock screen failing to paint.
 *
 * It also has to work with no network: the effective policy is cached locally
 * the last time the settings endpoint answered, so a phone in airplane mode
 * still locks and still unlocks.
 */
import { isCapacitor } from '../api/config';
import { AUTH_STORAGE_KEYS } from '../auth/storageKeys';
import type { UserManagementMobileAppLockPolicy } from '../../generated/config-enums';

/**
 * App-lock keys. Deliberately NOT in `shared/auth/storageKeys` — that module is
 * the canonical home of the auth SESSION keys and is a leaf by contract. None
 * of these embeds one of the eight literals `tools/check_auth_storage_keys.py`
 * guards. That check matches as a SUBSTRING and has no escape marker, so a name
 * that merely CONTAINS a session key's spelling fails the build — including,
 * as this comment originally proved, one written inside a comment.
 */
export const APP_LOCK_STORAGE_KEYS = {
  /** The effective policy, cached so the lock survives an offline cold start. */
  policy: 'panluma_app_lock_policy',
  /** Consecutive GENUINE unlock failures. Cancels never land here. */
  failures: 'panluma_app_lock_failures',
  /** Wall-clock ms at which the app was last backgrounded. */
  backgroundedAt: 'panluma_app_lock_backgrounded_at',
} as const;

/**
 * sessionStorage, not localStorage. This is the discriminator between a real
 * COLD START and an in-session document reload, and the app reloads itself a
 * lot: an OTA bundle swap, the version checker, a failed lazy chunk, the
 * hard-refresh button, switching workspace, starting and ending an emulation.
 * Treating every one of those as a cold start would prompt for Face ID in the
 * middle of a session, which is exactly what "background and cold start only"
 * rules out. sessionStorage survives `location.reload()` and dies with the
 * process, which is the distinction we need.
 */
const SESSION_MARKER_KEY = 'panluma_app_lock_session';

/** The effective configuration, as resolved by the backend and cached here. */
export interface AppLockPolicy {
  policy: UserManagementMobileAppLockPolicy;
  idleMinutes: number;
  userPreference: boolean | null;
  enabled: boolean;
  lockedByPolicy: boolean;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * The last policy the server told us, or null if it never has.
 *
 * Null is a real state: a device that upgraded into this feature with a session
 * already established has not called the endpoint yet. See `shouldLockOnBoot`
 * for which way that fails.
 */
export function readCachedPolicy(): AppLockPolicy | null {
  return readJson<AppLockPolicy>(APP_LOCK_STORAGE_KEYS.policy);
}

export function writeCachedPolicy(policy: AppLockPolicy): void {
  localStorage.setItem(APP_LOCK_STORAGE_KEYS.policy, JSON.stringify(policy));
}

export function clearCachedPolicy(): void {
  localStorage.removeItem(APP_LOCK_STORAGE_KEYS.policy);
}

/**
 * Is there a session on this device at all?
 *
 * Read from the ACCESS token, which is the one the bootstrap hydrates from and
 * the only one readable synchronously (the refresh token is in the keystore).
 * Without this the lock screen would cover the login page on a fresh install.
 */
function hasStoredSession(): boolean {
  return !!localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
}

/** True when the lock applies on this platform at all. Native builds only. */
export function isAppLockSupportedPlatform(): boolean {
  return isCapacitor;
}

/**
 * Mark this document as belonging to an already-running process.
 *
 * Called once, as early as possible. Every subsequent reload of this WebView
 * sees the marker and is correctly read as in-session rather than as a launch.
 */
export function markProcessStarted(): void {
  try {
    sessionStorage.setItem(SESSION_MARKER_KEY, '1');
  } catch {
    // A WebView with sessionStorage disabled degrades to "every reload is a
    // cold start" — more prompts, never fewer. Failing the safe way.
  }
}

function isFirstDocumentOfProcess(): boolean {
  try {
    return sessionStorage.getItem(SESSION_MARKER_KEY) === null;
  } catch {
    return true;
  }
}

/**
 * Should the app come up locked?
 *
 * Fails OPEN when there is no cached policy. That case is precisely an install
 * that upgraded into this feature with a live session, and the alternative —
 * failing closed — locks somebody out of their own offline data with no way to
 * reach the endpoint that would let them back in. One unlocked cold start, then
 * the cache populates on the next online session and the lock engages.
 */
export function shouldLockOnBoot(): boolean {
  if (!isAppLockSupportedPlatform()) return false;
  if (!hasStoredSession()) return false;
  if (!isFirstDocumentOfProcess()) return false;
  const policy = readCachedPolicy();
  return policy !== null && policy.enabled;
}

export function noteAppBackgrounded(now: number = Date.now()): void {
  localStorage.setItem(APP_LOCK_STORAGE_KEYS.backgroundedAt, String(now));
}

export function clearBackgroundedAt(): void {
  localStorage.removeItem(APP_LOCK_STORAGE_KEYS.backgroundedAt);
}

/**
 * Should returning to the foreground re-lock?
 *
 * Only elapsed BACKGROUND time counts. A foregrounded app that sits untouched
 * is never locked: the acceptance criteria test only the background path, and
 * locking over a half-filled purchase order is a data-loss risk in an ERP that
 * a one-second convenience gate does not earn.
 *
 * `idleMinutes: 0` means lock on every return, and is a real setting rather
 * than an unset one — so the comparison is `>=`, and a falsy check anywhere in
 * this chain would silently restore the 15-minute default.
 */
export function shouldLockOnForeground(now: number = Date.now()): boolean {
  if (!isAppLockSupportedPlatform()) return false;
  if (!hasStoredSession()) return false;
  const policy = readCachedPolicy();
  if (policy === null || !policy.enabled) return false;
  const raw = localStorage.getItem(APP_LOCK_STORAGE_KEYS.backgroundedAt);
  if (raw === null) return false;
  const backgroundedAt = Number(raw);
  if (!Number.isFinite(backgroundedAt)) return true;
  return now - backgroundedAt >= policy.idleMinutes * 60_000;
}

/** Consecutive genuine failures before the stored session is destroyed. */
export const MAX_UNLOCK_FAILURES = 5;

export function readUnlockFailures(): number {
  const raw = localStorage.getItem(APP_LOCK_STORAGE_KEYS.failures);
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * Count one genuine failure and report whether that exhausts the allowance.
 *
 * Only `promptUnlock`'s `failed` outcome reaches here — never a cancel. For
 * scale: Salesforce erases local data after ten failed PASSCODE attempts and
 * does not count biometric failures toward it at all.
 */
export function recordUnlockFailure(): { failures: number; exhausted: boolean } {
  const failures = readUnlockFailures() + 1;
  localStorage.setItem(APP_LOCK_STORAGE_KEYS.failures, String(failures));
  return { failures, exhausted: failures >= MAX_UNLOCK_FAILURES };
}

export function resetUnlockFailures(): void {
  localStorage.removeItem(APP_LOCK_STORAGE_KEYS.failures);
}

/**
 * A tenant on `required` whose device can do neither biometry nor a passcode.
 *
 * The policy cannot be honoured on such a handset, and letting it through would
 * leave the control unenforced on exactly the least secure devices it was
 * written for. So the device keeps no session instead: the person signs in
 * fully every time they open the app. The workspace's requirement is met — just
 * by re-authentication rather than by a local gate.
 */
export function requiresFullLoginInsteadOfLock(
  policy: AppLockPolicy | null,
  canAuthenticate: boolean
): boolean {
  if (!isAppLockSupportedPlatform()) return false;
  if (policy === null) return false;
  return policy.policy === 'required' && !canAuthenticate;
}

/**
 * Is the app lock currently covering the screen?
 *
 * A module-level flag rather than React context, because its one consumer —
 * `AuthContext`'s cross-tab `visibilitychange` handler — must be able to ask
 * without the app-lock provider being an ancestor it depends on.
 *
 * That handler reloads the document when stored auth and React state disagree.
 * While the lock is up they legitimately disagree: the token is in storage and
 * the session has not been hydrated. On mobile, EVERY return to the foreground
 * fires `visibilitychange`, so without this flag reopening the app would
 * reload, come up locked, reload again — a loop the person cannot escape.
 */
let lockedNow = false;

export function setLockedForCrossTabSync(locked: boolean): void {
  lockedNow = locked;
}

export function isAppLockEngaged(): boolean {
  return lockedNow;
}

/** Reset the lock's own storage. Used on sign-out and after a lockout. */
export function clearAppLockState(): void {
  localStorage.removeItem(APP_LOCK_STORAGE_KEYS.failures);
  localStorage.removeItem(APP_LOCK_STORAGE_KEYS.backgroundedAt);
  clearCachedPolicy();
}
